/**
 * Admin V3 — Bons de commande — list page / operator work queue
 *
 * Source: GET /admin/bon-de-commande → { bon_de_commande, count, total, limit, offset }
 * (ADMIN_PARTNER_LIST). F-1 work queue: status filter + free-text search +
 * offset/limit pagination so operators reliably discover pending work (e.g.
 * DRAFT BdC awaiting issue) and nothing disappears beyond the list limit.
 * Permission-gated "Créer depuis un devis" (ADMIN_PARTNER_UPDATE) →
 * POST /admin/bon-de-commande { devis_id }. Row → detail. Commercial layer.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Plus, ExternalLink, AlertCircle, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, SmallPill, EmptyState, currency, bdcStatusMeta } from '../../shared/visualTokens';
import { WorkQueueToolbar, WorkQueuePager } from '../../shared/WorkQueueControls';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const PERM_UPDATE = 'admin:partner:update';
const DEFAULT_LIMIT = 50;

const customerLabel = (c) => (c && (c.company || c.name)) || '—';

const BdcListPage = () => {
  const locale = DEFAULT_LOCALE;
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { showSuccess, showError } = useToast();
  const canCreate = can(PERM_UPDATE);

  // Work-queue state (F-1): status filter, debounced search, pagination offset.
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => { setSearch(searchInput.trim()); setOffset(0); }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const params = {
    limit: DEFAULT_LIMIT,
    offset,
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(search ? { search } : {}),
  };

  const { data, loading, error, refetch } = useAdminQuery('/admin/bon-de-commande', params);
  const items = Array.isArray(data?.bon_de_commande) ? data.bon_de_commande : [];
  const total = Number.isFinite(data?.total) ? data.total : (data?.count ?? items.length);
  const showSkeleton = loading && !data;

  const onStatus = (v) => { setStatusFilter(v); setOffset(0); };

  const [createOpen, setCreateOpen] = useState(false);
  const [devisId, setDevisId] = useState('');
  const [poRef, setPoRef] = useState('');
  const createM = useAdminMutation('/admin/bon-de-commande', 'post');

  const handleCreate = () => {
    createM.mutate({ devis_id: devisId.trim(), customer_po_reference: poRef.trim() || undefined }, {
      onSuccess: (res) => { showSuccess(t('bdc.create.ok', locale)); const id = res?.bon_de_commande?.id || res?.id; setCreateOpen(false); setDevisId(''); setPoRef(''); if (id) navigate(`/admin/billing/commande/${id}`); else refetch(); },
      onError: (e) => showError(e?.response?.data?.detail || t('bdc.create.error', locale)),
    });
  };

  return (
    <SectionCard
      title={t('bdc.title', locale)} icon={ClipboardList} iconClass="bg-purple-100 text-purple-600"
      action={canCreate ? (
        <Button variant="default" size="sm" onClick={() => setCreateOpen((v) => !v)} data-testid="adminv3-bdc-create-toggle">
          <Plus className="h-3.5 w-3.5 mr-1.5" />{t('bdc.action.create', locale)}
        </Button>
      ) : null}
    >
      <div className="space-y-4">
        {createOpen && canCreate && (
          <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-3" data-testid="adminv3-bdc-create-form">
            <p className="text-[12px] text-slate-500">{t('bdc.create.hint', locale)}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('bdc.create.devisId', locale)}</label>
                <input value={devisId} onChange={(e) => setDevisId(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-bdc-create-devis" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('bdc.create.poRef', locale)}</label>
                <input value={poRef} onChange={(e) => setPoRef(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handleCreate} disabled={createM.loading || !devisId.trim()} data-testid="adminv3-bdc-create-submit">
                {createM.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}{t('bdc.create.submit', locale)}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCreateOpen(false)}>{t('bdc.create.cancel', locale)}</Button>
            </div>
          </div>
        )}

        <WorkQueueToolbar
          statusValue={statusFilter} onStatusChange={onStatus} statusMeta={bdcStatusMeta}
          search={searchInput} onSearchChange={setSearchInput}
          testidPrefix="adminv3-bdc" locale={locale}
        />

        {data && (
          <div className="text-[12px] text-slate-500" data-testid="adminv3-bdc-summary">
            {t('bdc.summary', locale).replace('{n}', String(total))}
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3" data-testid="adminv3-bdc-error">
            <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-rose-700">{t('bdc.error.title', locale)}</p>
              <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={refetch} className="border-rose-200 bg-white text-rose-700 hover:bg-rose-50">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{t('bdc.error.retry', locale)}
            </Button>
          </div>
        )}

        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {[
                  t('bdc.col.number', locale), t('bdc.col.devis', locale), t('bdc.col.customer', locale),
                  t('bdc.col.total', locale), t('bdc.col.status', locale), t('bdc.col.actions', locale),
                ].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showSkeleton ? (
                Array(4).fill(0).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-slate-100">
                    {Array(6).fill(0).map((_, j) => (<td key={j} className="px-4 py-3.5"><div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" /></td>))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8">
                  <EmptyState icon={ClipboardList} title={t('bdc.empty.title', locale)} desc={t('bdc.empty.desc', locale)} />
                </td></tr>
              ) : (
                items.map((b) => {
                  const key = b.id || b.bdc_number;
                  return (
                    <tr key={key} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3.5">
                        <span className="inline-block rounded-lg bg-purple-50 px-2 py-1 font-mono text-[12px] font-bold text-purple-700">
                          {b.bdc_number || t('bdc.draftBadge', locale)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 font-mono text-[12px] text-slate-600">{b.devis_number || '—'}</td>
                      <td className="px-4 py-3.5 text-slate-900 font-medium text-[13px]">{customerLabel(b.customer)}</td>
                      <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap">{currency(b.total_ttc_estimated ?? 0)}</td>
                      <td className="px-4 py-3.5"><SmallPill meta={bdcStatusMeta[b.status] || bdcStatusMeta.draft} /></td>
                      <td className="px-4 py-3.5">
                        <button type="button" onClick={() => navigate(`/admin/billing/commande/${key}`)}
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                                title={t('bdc.action.open', locale)} data-testid={`adminv3-bdc-open-${key}`}>
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <WorkQueuePager
          offset={offset} limit={DEFAULT_LIMIT} total={total} shown={items.length}
          onPrev={() => setOffset((o) => Math.max(0, o - DEFAULT_LIMIT))}
          onNext={() => setOffset((o) => o + DEFAULT_LIMIT)}
          testidPrefix="adminv3-bdc" locale={locale}
        />
      </div>
    </SectionCard>
  );
};

export default BdcListPage;
